import type { ProgrammaticLandingPageConfig } from '../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from './storageSizeFacts'

export const KWH_7_SPEICHER_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '7-kwh-speicher-kosten',
  lang: 'de',
  topicId: 'kwh-speicher-7',
  title: '7 kWh Batteriespeicher: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet ein 7-kWh-Batteriespeicher? Für wen sich diese Speichergröße eignet, ' +
    'Eigenverbrauchs-Richtwerte und kostenloser Preisvergleich für Anlage + Speicher.',
  keywords: [
    '7 kWh Speicher Kosten',
    'Batteriespeicher 7 kWh Preis',
    '7 kWh Solarspeicher',
    'Was kostet ein 7 kWh Speicher',
  ],
  eyebrow: 'Speichergröße 7 kWh',
  h1: '7 kWh Batteriespeicher: Kosten und Richtwerte',
  intro:
    'Ein 7-kWh-Speicher ist eine verbreitete Größe für durchschnittliche Haushalte in Kombination ' +
    `mit einer ${STORAGE_REFERENCE_KWP}-kWp-Referenzanlage. Hier finden Sie Richtwerte zur ` +
    'passenden Haushaltsgröße, zum Eigenverbrauch mit Speicher und direkt darunter einen Rechner ' +
    'mit dem für diese Kombination vorbelegten Durchschnittspreis aus echten Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 7 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich ein 7-kWh-Speicher?' },
    {
      type: 'paragraph',
      text: 'Richtwert: 7 kWh passen typischerweise zu durchschnittlichen 3-4-Personen-Haushalten ' +
        'ohne Wärmepumpe oder E-Auto. Wer zusätzlich ' +
        'ein E-Auto teilweise zuhause lädt oder abends erhöhten Verbrauch hat, findet auf der ' +
        '12-kWh-Seite die nächstgrößere Stufe.',
    },
    { type: 'heading', text: 'Eigenverbrauch mit Batteriespeicher' },
    {
      type: 'paragraph',
      text:
        `Ohne Speicher liegt der Eigenverbrauchsanteil bei einer ${STORAGE_REFERENCE_KWP}-kWp-Anlage ` +
        `typischerweise bei rund ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %, mit Batteriespeicher ` +
        `im Rechenmodell rund ${SELF_CONSUMPTION_BATTERY_BONUS * 100} Prozentpunkte höher bei etwa ` +
        `${SELF_CONSUMPTION_WITH_BATTERY * 100} % - mit E-Auto oder Wärmepumpe im Haushalt kann der ` +
        'Anteil bis zu einer Obergrenze von 85 % weiter steigen. Eine genaue, auf Ihren Verbrauch ' +
        'zugeschnittene Berechnung liefert der PV-Rechner.',
    },
    { type: 'heading', text: 'Notstromfähigkeit' },
    {
      type: 'paragraph',
      text:
        'Bei 7 kWh reicht die Kapazität vieler Notstrom-Backup-Boxen bereits für mehrere zentrale ' +
        'Verbraucher über einen längeren Zeitraum, nicht aber zwingend für den kompletten ' +
        'Hausanschluss - abhängig vom tatsächlichen Verbrauch der angeschlossenen Geräte. Ob und ' +
        'in welchem Umfang ein Angebot Notstromfähigkeit einschließt, ist je Handwerker ' +
        'unterschiedlich - gezielt beim Angebot nachfragen.',
    },
    { type: 'heading', text: 'Speicherkosten und Förderung' },
    {
      type: 'paragraph',
      text:
        'Speicherkosten schwanken je nach Hersteller und Installationsaufwand stark - Marktrecherche ' +
        `2026 zeigt eine Spanne von etwa ${BATTERY_COST_PER_KWH_RANGE_EUR.min} bis ` +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('de-DE')} € pro kWh. Speicherspezifische ` +
        'Förderungen gibt es regional unterschiedlich (z. B. Berliner Landesprogramm mit Zuschuss ' +
        'für Batteriespeicher) - Details im Förder-Überblick, eine genaue Berechnung für Ihre ' +
        'Kombination liefert der Rechner unten.',
    },
  ],
  faq: [
    {
      q: 'Ist 7 kWh die richtige Speichergröße für einen Standardhaushalt?',
      a: 'Für einen durchschnittlichen 3-4-Personen-Haushalt ohne Wärmepumpe oder E-Auto ist 7 kWh eine gängige Größe. Mit zusätzlichen Großverbrauchern lohnt sich ein Blick auf 12 oder 15 kWh.',
    },
    {
      q: 'Reicht 7 kWh, um über Nacht komplett autark zu sein?',
      a: 'In vielen Fällen ja, bei durchschnittlichem Verbrauch ohne Wärmepumpe oder E-Auto-Ladung. Eine genaue, auf Ihren tatsächlichen Verbrauch zugeschnittene Einschätzung liefert der PV-Rechner.',
    },
    {
      q: 'Mit welcher Anlagengröße wird ein 7-kWh-Speicher meist kombiniert?',
      a: `Auf dieser Seite als Referenz eine ${STORAGE_REFERENCE_KWP}-kWp-Anlage. Bei etwas kleineren Anlagen passt 7 kWh ebenfalls gut - eine genaue Empfehlung für Ihr Dach liefert der PV-Rechner.`,
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '5 kWh Batteriespeicher: Kosten & Richtwerte', href: '/5-kwh-speicher-kosten' },
    { label: '12 kWh Batteriespeicher: Kosten & Richtwerte', href: '/12-kwh-speicher-kosten' },
    { label: '10 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/10-kwp-photovoltaik-kosten' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
  ],
}
